import type { FC } from 'react';
import { Bar } from 'react-chartjs-2';
import type { ConstructBreakdown } from './licenseBreakdownData';

export type ConstructTypeBreakdownChartProps = {
  breakdown: ConstructBreakdown;
};

const CHART_HEIGHT = 260;
const TOTAL_LABEL_OFFSET = 8;
const TOTAL_LABEL_FONT_SIZE = 12;

/**
 * chart.js 2 has no data labels, so the "N total" caption above each bar is drawn here. Everything it
 * needs is read back off the chart, which keeps the plugin stateless - react-chartjs-2 hands
 * `plugins` to the Chart constructor, so a plugin closing over props could otherwise go stale.
 */
type BarModel = { x: number; y: number };
type BarMeta = { data: Array<{ _model?: BarModel }> };
type StackedBarChart = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  data: { datasets: Array<{ data: number[]; backgroundColor: string }> };
  getDatasetMeta: (index: number) => BarMeta;
};

const barTotalsPlugin = {
  afterDatasetsDraw: (chart: StackedBarChart) => {
    const { ctx, data } = chart;
    const barCount = data.datasets[0]?.data.length ?? 0;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = `600 ${TOTAL_LABEL_FONT_SIZE}px ${getComputedStyle(chart.canvas).fontFamily}`;

    for (let barIndex = 0; barIndex < barCount; barIndex += 1) {
      const stacked = data.datasets
        .map((dataset, datasetIndex) => ({
          value: dataset.data[barIndex] ?? 0,
          color: dataset.backgroundColor,
          model: chart.getDatasetMeta(datasetIndex).data[barIndex]?._model,
        }))
        .filter((entry) => entry.value > 0 && entry.model !== undefined);

      const total = stacked.reduce((sum, entry) => sum + entry.value, 0);
      if (total === 0) {
        continue;
      }
      // The bottom-most segment carries the tier's darkest shade, which is what the caption uses.
      const base = stacked[0];
      const top = Math.min(...stacked.map((entry) => entry.model?.y ?? 0));
      ctx.fillStyle = base?.color ?? '';
      ctx.fillText(
        `${total.toLocaleString()} total`,
        base?.model?.x ?? 0,
        top - TOTAL_LABEL_OFFSET,
      );
    }
    ctx.restore();
  },
};

/** Stacked bar per cloud tier, split by construct type. */
export const ConstructTypeBreakdownChart: FC<
  ConstructTypeBreakdownChartProps
> = ({ breakdown }) => {
  const data = {
    labels: breakdown.bars.map((bar) => bar.label),
    // One dataset per segment, contributing only to its own tier's bar.
    datasets: breakdown.bars.flatMap((bar, barIndex) =>
      bar.segments.map((segment) => ({
        label: segment.label,
        backgroundColor: segment.color,
        data: breakdown.bars.map((_, index) =>
          index === barIndex ? segment.count : 0,
        ),
      })),
    ),
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    legend: { display: false },
    animation: { duration: 0 },
    // Room for the "N total" captions, which are drawn outside the bars.
    layout: { padding: { top: TOTAL_LABEL_FONT_SIZE + TOTAL_LABEL_OFFSET } },
    scales: {
      xAxes: [
        {
          stacked: true,
          gridLines: { display: false },
          ticks: { fontStyle: '600' },
        },
      ],
      yAxes: [{ stacked: true, display: false }],
    },
    tooltips: {
      callbacks: {
        label: (
          item: { datasetIndex: number; yLabel: number },
          chartData: { datasets: Array<{ label: string }> },
        ) =>
          ` ${chartData.datasets[item.datasetIndex]?.label ?? ''}: ${item.yLabel.toLocaleString()}`,
      },
    },
  };

  return (
    // See DeviceTypeBreakdownChart: the box has to be allowed to shrink below the canvas's current
    // width for chart.js to notice, and clip it until it has redrawn.
    <div className='min-w-0 overflow-hidden' style={{ height: CHART_HEIGHT }}>
      <Bar data={data} options={options} plugins={[barTotalsPlugin]} />
    </div>
  );
};
